import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IConfigGlobalePortaleSaaS, UtilsService } from '@ebw-monorepo/utils';
import { BehaviorSubject, forkJoin, map, take } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../libs/ng-components/utils/src/lib/services/api-url.service';
import configGlobale from '../../../src/assets/config/config-global.json';
import { ISocketConfig } from './socket/socket.interface';
import { UserManagerService } from './user-manager.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  static CONFIGURATION_ITEM_STRING = 'configuration';
  static MAP_CONFIGURATION_ITEM_STRING = 'map_configuration';
  static WEBSOCKET_CONFIGURATION_ITEM_STRING = 'websocket_configuration';

  // https://mariusschulz.com/blog/the-unknown-type-in-typescript
  public baseConfiguration$: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);

  public mapConfiguration$: BehaviorSubject<Record<string, unknown> | null> = new BehaviorSubject<Record<
    string,
    unknown
  > | null>(null);

  public websocketConfiguration$: BehaviorSubject<ISocketConfig | null> = new BehaviorSubject<ISocketConfig | null>(
    null
  );

  constructor(
    private userManagerService: UserManagerService,
    private utilsService: UtilsService,
    private apiUrlService: ApiUrlService
  ) {}

  /**
   * Parses and return as a JS object the local configuration file contained in folder assets/config.
   * @returns The local configuration file in json format.
   */

  public getConfigGlobale(): IConfigGlobalePortaleSaaS {
    return configGlobale;
  }

  /**
   * Get the site configuration file from the API. This configuration contains information about several
   * objects and models necessary for the correct working of the application.
   * @param config_type specifies the type of configuration that should be retrieved from DB (possible values : base / map / websocket)
   */
  public getApiConfigurationFile(config_type: string = 'base') {
    const API_URL = this.apiUrlService.createUrl(API_URLS.CONFIG.INIT);

    const body = new HttpParams().set('config_type', config_type);
    return this.utilsService.postRequest(API_URL, body, false);
  }

  public getApiMapConfigurationFile() {
    return this.getApiConfigurationFile('map');
  }

  /**
   * Get the sessionStorage variable this.CONFIGURATION_ITEM_STRING
   */
  public getConfiguration(): any {
    return this.baseConfiguration$.value;
  }

  public getWebSocketConfiguration(): ISocketConfig {
    return this.websocketConfiguration$.value;
  }

  public getMapConfiguration() {
    return this.mapConfiguration$.value;
  }

  public setConfiguration(configJSON: any) {
    this.baseConfiguration$.next(configJSON);
  }

  public setMapConfiguration(configJSON: any) {
    this.mapConfiguration$.next(configJSON);
  }

  public setWebsocketConfiguration(configJSON: ISocketConfig) {
    this.websocketConfiguration$.next(configJSON);
  }

  /**
   * Remove this.CONFIGURATION_ITEM_STRING item from the sessionStorage.
   */
  public removeConfiguration() {
    // sessionStorage.removeItem(ConfigService.CONFIGURATION_ITEM_STRING);
    this.baseConfiguration$.next(null);
    this.mapConfiguration$.next(null);
    this.websocketConfiguration$.next(null);
  }

  public getAllConfigurations() {
    const loadApiConfigurationFile$ = this.getApiConfigurationFile();
    const loadApiMapConfigurationFile$ = this.getApiMapConfigurationFile();
    // const loadApiWebsocketConfigurationFile$ = this.getApiWebsocketConfigurationFile();

    return forkJoin([loadApiConfigurationFile$, loadApiMapConfigurationFile$]).pipe(
      take(1),
      map((res) => {
        //loadApiConfigurationFile (base)
        if (res[0].data) {
          this.setConfiguration(res[0].data);
          const userInfo = res[0].data.userInfo;
          const baseConfig = res[0].data.config;

          // Load User Info from configuration
          this.userManagerService.setUserInfo(userInfo);

          // Setting user id
          this.userManagerService.userId = userInfo.id;

          const socket = baseConfig.find((el) => el.key == 'socket');
          if (socket) {
            this.setWebsocketConfiguration(socket);
          }
        }

        //loadApiMapConfigurationFile (map)
        if (res[1].data) {
          this.setMapConfiguration(res[1].data);
        }
      })
    );
  }

  public getMapConfigurationFromApi() {
    return this.getApiMapConfigurationFile().pipe(
      take(1),
      map((res) => {
        this.setMapConfiguration(res.data);
      })
    );
  }
}
